import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import compose from 'recompose/compose'
import ImmutablePropTypes from 'react-immutable-proptypes'
import isFunction from 'lodash/isFunction'
import { connect as connectStaticText } from '../StaticText/connect'
import Button, { TYPES } from '../Button/index'
import LivePageTimer from '../EventsPage/LivePageTimer/index'
import { H1, HEADING_TYPES } from '../Heading/index'
import { URL_LIVE_PAGE } from '../../services/url/constants'

const toRelativeUrl = (fullUrl) => {
  if (!fullUrl) return null
  try {
    const url = new URL(fullUrl)
    return url.pathname + url.search + url.hash
  } catch {
    return null
  }
}

export const Countdown = ({ countDown, staticText }) => (
  <div className="spotlight-live-access-v2__countdown">
    {staticText.getIn(['data', 'countdownLabel'])} <LivePageTimer startDate={new Date().getTime() + countDown} />
  </div>
)

export const StreamingNow = ({ staticText }) => (
  <div className="spotlight-live-access-v2__streaming-now">
    <span className="spotlight-live-access-v2__dot" /> {staticText.getIn(['data', 'streamingNow'])}
  </div>
)

export const Status = ({ countDown, staticText }) => {
  return (
    <div className="spotlight-live-access-v2__status">
      {countDown
        ? <Countdown countDown={countDown} staticText={staticText} />
        : <StreamingNow staticText={staticText} />
      }
    </div>
  )
}

const SpotlightLiveAccessV2 = ({
  data,
  staticText,
  onVisibilityChange,
  visibilityThreshold,
}) => {
  const [isVisible, setIsVisible] = useState(false)
  const ref = useRef(null)
  const countdown = data && data.get('countdown')

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          observer.unobserve(ref.current)
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: visibilityThreshold,
      },
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current)
      }
    }
  }, [])

  useEffect(() => {
    if (isVisible && isFunction(onVisibilityChange)) {
      // GA4 spotlight impression event
      onVisibilityChange()
    }
  }, [isVisible])

  if (!data) {
    return null
  }

  return (
    <div
      className="spotlight-live-access-v2"
      style={{
        backgroundImage: `url(${data.getIn(['mobileBackgroundUrl'])})`,
      }}
      ref={ref}
    >
      <div className="spotlight-live-access-v2__overlay"/>
      <div className="spotlight-live-access-v2__overlay-darken" />
      <div className="spotlight-live-access-v2__content">
        <Status countDown={countdown} staticText={staticText} />
        <H1
          as={HEADING_TYPES.H4}
          inverted
          className="spotlight-ad-v2__title"
        >
          {data.getIn(['title'])}
        </H1>
        <div className="spotlight-live-access-v2__subtitle">
          {data.getIn(['subTitle'])}
        </div>
        <div className="spotlight-live-access-v2__description">
          {data.getIn(['description'])}
        </div>
        <Button
          className="spotlight-live-access-v2__cta"
          type={TYPES.PRIMARY}
          url={toRelativeUrl(data.get('webviewUrl')) || URL_LIVE_PAGE}
        >
          {countdown ? staticText.getIn(['data', 'moreInfo']) : staticText.getIn(['data', 'watchNow'])}
        </Button>
      </div>
    </div>
  )
}

SpotlightLiveAccessV2.propTypes = {
  data: ImmutablePropTypes.map.isRequired,
  onVisibilityChange: PropTypes.func,
  visibilityThreshold: PropTypes.number,
}

SpotlightLiveAccessV2.defaultProps = {
  visibilityThreshold: 0.80,
}

export default compose(
  connectStaticText({ storeKey: 'spotlightLiveAccessV2' }),
)(SpotlightLiveAccessV2)
